// transport-config-helpers.js
//
// Pure helper functions for the configuration logic of the package transport wizard.
//
// Domain terms used throughout this module: a "Werk" is a plant with its own tenant;
// artifact ids get its "Kennung" (a short suffix) appended. A "Stufe" is a deployment
// stage (dev, qas, preprod, prod, depending on the space). A config value is set once
// at the base stage and inherited by every later stage; a later stage can override it,
// and only the deviation itself is stored (see effektiverBasiswert and buildConfigsByWerk
// below for how the base value and the deviations are computed).
//
// Important:
//  - Pure functions only (no React, no hooks, no global stores).
//  - No external imports, so the module stays build- and tree-shake-friendly.
//  - Defensive against null/undefined inputs.

/**
 * Builds the cache key for an artifact configuration.
 *
 * Format:
 *   - with werkKennung: "artId__werkKennung"
 *   - without werkKennung, with targetId: "artId__targetId"
 *   - otherwise: "artId"
 *
 * @param {string} artId            Artifact id (e.g. flow id).
 * @param {string} [targetId]       Target instance id, optional.
 * @param {string} [werkKennung]    Werk kennung (e.g. "werk__kennung"), optional.
 *                                  Takes precedence over targetId.
 * @returns {string} Cache key, or "" if artId is missing.
 *
 * @example
 *   getArtifactCacheKey("Flow1", "tgt-1");           // "Flow1__tgt-1"
 *   getArtifactCacheKey("Flow1", "tgt-1", "werk_DE"); // "Flow1__werk_DE"
 *   getArtifactCacheKey("Flow1");                     // "Flow1"
 */
export function getArtifactCacheKey(artId, targetId, werkKennung) {
  if (!artId) return "";
  if (werkKennung) return `${artId}__${werkKennung}`;
  if (targetId) return `${artId}__${targetId}`;
  return String(artId);
}

/**
 * Normalizes a config entry from the backend into the shape the transport UI uses.
 *
 * Fields added:
 *   - newValue:     targetValue || ""
 *   - fromTarget:   !!targetValue
 *   - defaultValue: ParameterValue
 *   - modified:     false (initial state, before the user edits anything)
 *
 * @param {object|null|undefined} raw Raw entry from the backend.
 * @returns {object|null} Normalized entry, or null if raw is empty.
 *
 * @example
 *   normalizeConfigEntry({ ParameterKey: "host", ParameterValue: "x", targetValue: "y" })
 *   // -> { ParameterKey:"host", ParameterValue:"x", targetValue:"y",
 *   //      newValue:"y", fromTarget:true, defaultValue:"x", modified:false }
 */
export function normalizeConfigEntry(raw) {
  if (!raw || typeof raw !== "object") return null;
  const targetValue = raw.targetValue;
  return {
    ...raw,
    newValue: targetValue || "",
    fromTarget: !!targetValue,
    defaultValue: raw.ParameterValue,
    modified: false,
  };
}

/**
 * Classifies the status of a config row for the UI label.
 *
 * Rule order (the first match wins):
 *   1. !newValue and group in {sender,receiver} -> "required"
 *      (a hint that the field is normally mandatory; does not block the
 *      transport itself, see validateRequiredFields below)
 *   2. !newValue                                -> "empty"
 *   3. fromTarget and newValue===targetValue    -> "fromTarget"
 *   4. newValue !== defaultValue                -> "modified"
 *   5. otherwise                                -> "default"
 *
 * @param {object|null|undefined} c Normalized config entry.
 * @returns {"required"|"fromTarget"|"modified"|"default"|"empty"}
 *
 * @example
 *   classifyConfigStatus({ group:"sender", newValue:"" });
 *   // -> "required"
 *   classifyConfigStatus({ group:"other", newValue:"x", defaultValue:"x" });
 *   // -> "default"
 */
export function classifyConfigStatus(c) {
  if (!c || typeof c !== "object") return "empty";
  const group = c.group;
  const newValue = c.newValue;
  const fromTarget = !!c.fromTarget;
  const defaultValue = c.defaultValue;
  const targetValue = c.targetValue;
  // hasTargetValue: the parameter already exists on the target tenant (the backend
  // returned a targetValue). Such a parameter is never mandatory: the existing value
  // on the target is kept when the user leaves the field empty.
  const hasTargetValue = targetValue !== undefined && targetValue !== null && targetValue !== "";
  if (!newValue) {
    if ((group === "sender" || group === "receiver") && !hasTargetValue && !fromTarget) return "required";
    return "empty";
  }
  if (fromTarget && newValue === targetValue) return "fromTarget";
  if (newValue !== defaultValue) return "modified";
  return "default";
}

/**
 * The value that applies to the first target stage and is inherited by every later stage.
 *
 * Sender and receiver parameters deliberately do NOT inherit the source value: an address from
 * development would simply be wrong in the target, so the field stays empty and shows up as a
 * required field.
 *
 * @param {object} c Normalized config entry.
 * @returns {string} Base value, "" when none applies.
 */
export function effektiverBasiswert(c) {
  if (!c) return "";
  if (c.newValue) return c.newValue;
  const istAdresse = c.group === "sender" || c.group === "receiver";
  return istAdresse ? "" : (c.defaultValue || "");
}

/**
 * Builds the parameter structure the cicd-snapshot endpoint expects as `configsByWerk`.
 *
 * Target shape:
 *   {
 *     "<suffix>" | "": {                        // "" = no plant split
 *       "<source-artifact-id>": [
 *         { ParameterKey, DataType, Description,
 *           values: { "__base__": "...", "<stageKey>": "..." } }
 *       ]
 *     }
 *   }
 *
 * The backend turns this into SAP's file: `__base__` becomes ParameterValues.Default, every
 * stage key becomes the name of the GitHub environment (qas -> QAS, preprod -> PRE-PROD).
 *
 * Two differences from the earlier overlay format, both intentional:
 *
 *  - The plant level sits at the OUTSIDE instead of inside the file content. In Git every plant
 *    gets its own package folder with its own file; inside the file there are no plants left.
 *  - EVERY parameter with a value is written, not only the changed ones. SAP's read rule falls
 *    back to ParameterValues.Default - without it the parameter stays unset in the target. The
 *    source value is exactly that fallback.
 *
 * Stage values equal to the base value are not written: they would only inflate the Git diff,
 * and SAP's fallback yields the same value anyway.
 *
 * @param {object} state    TransportV2 reducer state (only state.cfg is read).
 * @param {Array}  [stages] Forward stages from vorwaertsStufen(state). The first one is the base.
 * @returns {object} Parameter map per plant (empty when nothing is configured).
 */
export function buildConfigsByWerk(state, stages, gitCfg) {
  const out = {};
  const cfg = state && state.cfg;
  if (!cfg || typeof cfg !== "object") return out;
  const stufen = Array.isArray(stages) ? stages : [];
  // The first forward stage is the base. Every later one is a possible deviation.
  const overlayStufen = stufen.slice(1);
  const git = gitCfg || (state && state.gitCfg) || {};
  for (const [werkKey, artefactMap] of Object.entries(cfg)) {
    // "std" means no plant split. In the backend that is the empty suffix.
    const kennung = werkKey === "std" ? "" : werkKey;
    for (const [artId, item] of Object.entries(artefactMap || {})) {
      const entries = (item && item.entries) || [];
      const params = [];
      for (const c of entries) {
        if (!c || !c.ParameterKey) continue;
        const values = {};
        const basis = effektiverBasiswert(c);
        if (basis !== "") values.__base__ = basis;
        // What is already in Git for this parameter? Only that tells "deleted" from "never set".
        const inGit = git?.[werkKey]?.[artId]?.[c.ParameterKey]?.values || null;
        for (const st of overlayStufen) {
          const wert = c.byStage && c.byStage[st.key] ? c.byStage[st.key].newValue : "";
          const leer = wert == null || wert === "" || wert === basis;
          if (leer) {
            // Tombstone: the stage was in Git and is now empty, so the user removed the
            // override deliberately. Without this null the old value would stay and the stage
            // would silently keep running a value nobody wanted.
            if (inGit && inGit[st.key] != null) values[st.key] = null;
            continue;
          }
          values[st.key] = wert;
        }
        if (!Object.keys(values).length) continue;
        params.push({
          ParameterKey: c.ParameterKey,
          DataType: c.DataType || "xsd:string",
          Description: c.Description || "",
          values,
        });
      }
      if (!params.length) continue;
      if (!out[kennung]) out[kennung] = {};
      out[kennung][artId] = params;
    }
  }
  return out;
}

/**
 * Required-field validation.
 *
 * Mandatory field = entry with group in {sender,receiver} and an empty newValue.
 * This used to reject a transport when such a field was empty; that check was
 * removed (see the comment in the function body), so the return value below is
 * now constant.
 *
 * @param {Array|null|undefined} cfgs List of normalized configs (unused, kept for
 *        a stable call signature).
 * @returns {{ok:boolean, missing:string[]}} Always {ok:true, missing:[]}.
 */
export function validateRequiredFields(cfgs) {
  // Client-side mandatory-field validation was removed: the "sender/receiver without a
  // value is mandatory" heuristic was too strict. SAP CPI allows many optional
  // sender/receiver parameters (e.g. location id, optional headers). Actual mandatory
  // fields are validated by the target tenant when the transport call runs and produce
  // a clear error there. The "required" UI indicator stays as a hint (in
  // classifyConfigStatus) but no longer blocks the transport.
  return { ok: true, missing: [] };
}

/**
 * Buckets configs by group into exactly three groups: sender, receiver, other.
 * Deliberately no processing bucket and no credentials bucket: those groups are
 * not meant to be shown as their own section.
 *
 * @param {Array|null|undefined} cfgs List of normalized configs.
 * @returns {{sender:Array, receiver:Array, other:Array}}
 *
 * @example
 *   bucketByGroup([
 *     {ParameterKey:"a", group:"sender"},
 *     {ParameterKey:"b", group:"receiver"},
 *     {ParameterKey:"c", group:"processing"},
 *     {ParameterKey:"d"} // no group
 *   ]);
 *   // -> { sender:[a], receiver:[b], other:[c,d] }
 */
export function bucketByGroup(cfgs) {
  const sender = [];
  const receiver = [];
  const other = [];
  if (!cfgs || !Array.isArray(cfgs)) return { sender, receiver, other };
  for (const c of cfgs) {
    if (!c) continue;
    if (c.group === "sender") sender.push(c);
    else if (c.group === "receiver") receiver.push(c);
    else other.push(c);
  }
  return { sender, receiver, other };
}

/**
 * Extracts the adapter name from the first entry with a non-empty adapterType.
 *
 * @param {Array|null|undefined} cfgs List of normalized configs.
 * @returns {string} Adapter name, or "" if none is found.
 *
 * @example
 *   detectAdapter([{adapterType:""}, {adapterType:"HTTPS"}, {adapterType:"SOAP"}]);
 *   // -> "HTTPS"
 */
export function detectAdapter(cfgs) {
  if (!cfgs || !Array.isArray(cfgs)) return "";
  for (const c of cfgs) {
    if (c && c.adapterType) return String(c.adapterType);
  }
  return "";
}

/**
 * Builds the backend URL for artifact-configs.
 *
 * URL scheme:
 *   /api/{sourceIid}/transport/artifact-configs/{flowId}
 *   [?targetInstanceId=X&targetFlowId=Y]
 *
 * The query string is only appended when opts.isUpdate=true and opts.targetInstanceId
 * is set. opts.targetFlowId falls back to flowId.
 *
 * @param {string} sourceIid          Source instance id.
 * @param {string} flowId             Source artifact id.
 * @param {object} [opts]
 * @param {string} [opts.targetInstanceId]
 * @param {string} [opts.targetFlowId]
 * @param {boolean}[opts.isUpdate]    Default false.
 * @returns {string} Full URL.
 *
 * @example
 *   buildArtifactConfigsUrl("src", "Flow1",
 *     { targetInstanceId:"tgt", targetFlowId:"Flow1_DE", isUpdate:true });
 *   // -> "/api/src/transport/artifact-configs/Flow1?targetInstanceId=tgt&targetFlowId=Flow1_DE"
 */
export function buildArtifactConfigsUrl(sourceIid, flowId, opts) {
  const base = `/api/${sourceIid}/transport/artifact-configs/${encodeURIComponent(flowId)}`;
  if (!opts || !opts.isUpdate || !opts.targetInstanceId) return base;
  const tgtFlow = opts.targetFlowId || flowId;
  return `${base}?targetInstanceId=${opts.targetInstanceId}&targetFlowId=${encodeURIComponent(tgtFlow)}`;
}

// Self-test kept for reference (commented out, not executed):
//
// const sample = [
//   { ParameterKey:"host",  group:"sender",   ParameterValue:"old", targetValue:"new", DataType:"xsd:string", adapterType:"HTTPS" },
//   { ParameterKey:"port",  group:"other",    ParameterValue:"443", targetValue:"",    DataType:"xsd:string" },
//   { ParameterKey:"creds", group:"receiver", ParameterValue:"u",   targetValue:"",    DataType:"xsd:string" }
// ];
// const normalized = sample.map(normalizeConfigEntry);
// console.log("cacheKey ", getArtifactCacheKey("Flow1", "tgt", "werk_DE"));
// console.log("status   ", normalized.map(classifyConfigStatus));
// console.log("base     ", normalized.map(effektiverBasiswert));
// console.log("validate ", validateRequiredFields(normalized));
// console.log("buckets  ", bucketByGroup(normalized));
// console.log("adapter  ", detectAdapter(normalized));
// console.log("url      ", buildArtifactConfigsUrl("src","Flow1",{targetInstanceId:"tgt",targetFlowId:"Flow1_DE",isUpdate:true}));
